import { create } from 'zustand'
import { load, save } from '@/lib/storage'
import type { StoreDefinition } from '@/lib/storage'
import type { Patience } from './settingsStore'
import { PATIENCE_MS, PATIENCE_ORDER } from './settingsStore'

/**
 * The self-improving loop.
 *
 * Every turn of every conversation leaves a small record here: how long it
 * took, whether Namu had to ask again, whether the person gave up and typed.
 * None of it leaves the device. Two things read it.
 *
 * The diagnostics screen reads it so a person can see, in plain numbers,
 * whether the assistant is actually working for them.
 *
 * `suggestPatience` reads it so the app can tune itself. If Namu keeps having
 * to ask again, or the person keeps falling back to typing, the most likely
 * cause is that it stops listening before they have finished, and the fix is
 * to wait longer. That is a real accessibility lever, not a metric: the
 * published evidence is that widening the endpointing window removed about
 * four fifths of the cut-offs experienced by people who stutter, at a cost of
 * roughly a second of latency.
 */

/**
 * One turn of one conversation.
 *
 * This used to be defined by the onboarding director, which no longer exists.
 * It lives here now because the diagnostics loop is what actually consumes it,
 * and it should not depend on whichever part of the app happens to produce it.
 */
export interface TurnRecord {
  /** What was being asked or answered. Free-form: the open exchange has no steps. */
  stepId: string
  attempt: number
  outcome: TurnOutcome
  /** Time from the end of the question to a settled answer. */
  listenMs: number
  confidence: number
  at: number
}

export type TurnOutcome =
  | 'captured'
  | 'confirmed'
  | 'corrected'
  | 'retry'
  | 'skipped'
  | 'typed'
  | 'timeout'
  | 'aborted'

export interface ConversationTelemetry {
  recordTurn(record: TurnRecord): void
}

export interface DiagnosticsSnapshot {
  records: TurnRecord[]
  /** Times the person used the typed path instead of speaking. */
  typedFallbacks: number
  /** Times the person talked over Namu. */
  interruptions: number
  /** Times a transport event arrived that made no sense for the current state. */
  rejectedTransitions: number
}

/** Enough history to see a pattern, small enough to never trouble the quota. */
const MAX_RECORDS = 200

function emptySnapshot(): DiagnosticsSnapshot {
  return { records: [], typedFallbacks: 0, interruptions: 0, rejectedTransitions: 0 }
}

const DEFINITION: StoreDefinition<DiagnosticsSnapshot> = {
  key: 'diagnostics',
  version: 1,
  fallback: emptySnapshot,
  validate: (data): data is DiagnosticsSnapshot =>
    typeof data === 'object' && data !== null && Array.isArray((data as DiagnosticsSnapshot).records),
}

export interface DiagnosticsSummary {
  turns: number
  /** Median, not mean: one 30-second pause should not move the headline. */
  medianListenMs: number
  retries: number
  retryRate: number
  typedFallbacks: number
  interruptions: number
  rejectedTransitions: number
  /** Fraction of answers that needed reading back before they were kept. */
  confirmationRate: number
}

/**
 * Derives the summary from a snapshot.
 *
 * A free function, not only a store method, and that distinction matters.
 * Reading it through a zustand selector as `store.summary()` builds a fresh
 * object on every render, React sees a changed snapshot every time, and the
 * screen loops until it gives up and renders nothing at all. Components
 * memoise this against the raw fields instead.
 */
export function summarise(snapshot: DiagnosticsSnapshot): DiagnosticsSummary {
  const { records, typedFallbacks, interruptions, rejectedTransitions } = snapshot
  const turns = records.length
  const retries = records.filter(
    (record) => record.outcome === 'retry' || record.outcome === 'timeout',
  ).length
  const confirmations = records.filter(
    (record) => record.outcome === 'confirmed' || record.outcome === 'corrected',
  ).length

  return {
    turns,
    medianListenMs: Math.round(median(records.map((record) => record.listenMs))),
    retries,
    retryRate: turns === 0 ? 0 : retries / turns,
    typedFallbacks,
    interruptions,
    rejectedTransitions,
    confirmationRate: turns === 0 ? 0 : confirmations / turns,
  }
}

function median(values: number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) return sorted[middle] as number
  return (((sorted[middle - 1] as number) + (sorted[middle] as number)) / 2)
}

interface DiagnosticsState extends DiagnosticsSnapshot {
  recordTurn(record: TurnRecord): void
  recordTypedFallback(): void
  recordInterruption(): void
  recordRejectedTransitions(count: number): void
  summary(): DiagnosticsSummary
  /**
   * A patience setting that fits the evidence, or null when the current one is
   * fine. Never applied silently: the settings screen offers it.
   */
  suggestPatience(current: Patience): Patience | null
  clear(): void
}

/**
 * Below this many turns there is not enough evidence to conclude anything, and
 * suggesting a change would just be reacting to noise.
 */
const MIN_TURNS_FOR_SUGGESTION = 8
/** Above this share of turns needing a repair, something is genuinely wrong. */
const RETRY_RATE_TRIGGER = 0.28
/** Falling back to typing this often means speaking is not working. */
const FALLBACK_RATE_TRIGGER = 0.2

export const useDiagnosticsStore = create<DiagnosticsState>()((setState, getState) => {
  const persist = () => {
    const { records, typedFallbacks, interruptions, rejectedTransitions } = getState()
    save(DEFINITION, { records, typedFallbacks, interruptions, rejectedTransitions })
  }

  return {
    ...load(DEFINITION),

    recordTurn(record) {
      const records = [...getState().records, record].slice(-MAX_RECORDS)
      setState({ records })
      persist()
    },

    recordTypedFallback() {
      setState({ typedFallbacks: getState().typedFallbacks + 1 })
      persist()
    },

    recordInterruption() {
      setState({ interruptions: getState().interruptions + 1 })
      persist()
    },

    recordRejectedTransitions(count) {
      if (count <= getState().rejectedTransitions) return
      setState({ rejectedTransitions: count })
      persist()
    },

    /**
     * For callers outside React. Components must not use this through a
     * selector: it returns a new object each call, which React reads as a
     * changed snapshot on every render.
     */
    summary() {
      const { records, typedFallbacks, interruptions, rejectedTransitions } = getState()
      return summarise({ records, typedFallbacks, interruptions, rejectedTransitions })
    },

    suggestPatience(current) {
      const { turns, retryRate, typedFallbacks } = getState().summary()
      if (turns < MIN_TURNS_FOR_SUGGESTION) return null

      const fallbackRate = typedFallbacks / turns
      const struggling = retryRate >= RETRY_RATE_TRIGGER || fallbackRate >= FALLBACK_RATE_TRIGGER
      const index = PATIENCE_ORDER.indexOf(current)
      if (index === -1) return null

      if (struggling) {
        const next = PATIENCE_ORDER[index + 1]
        return next ?? null
      }

      // Going the other way is held to a much stricter bar. Being cut off is a
      // far worse experience than waiting a moment longer, so the app is
      // reluctant to make itself less patient.
      const comfortable = retryRate < 0.05 && fallbackRate < 0.02 && turns >= MIN_TURNS_FOR_SUGGESTION * 3
      if (comfortable && current !== 'quick') {
        const previous = PATIENCE_ORDER[index - 1]
        // Never step below the default on the app's own initiative.
        return previous && PATIENCE_MS[previous] >= PATIENCE_MS.normal ? previous : null
      }

      return null
    },

    clear() {
      setState(emptySnapshot())
      persist()
    },
  }
})
